import React from 'react';

// Tag pill. Colour is a hex string from the tag-safe palette (blue /
// rose / slate / sand — enforced by the DB whitelist CHECK on
// tags.colour). Rendered via inline styles since Tailwind can't
// generate classes for runtime arbitrary hex.
//
// The legacy NAMED_HEX fallback ('teal' / 'neutral' / etc.) was
// dropped in 2026-06 alongside the whitelist migration — named
// values can't exist in the DB anymore, and the fallback was the
// last code path that could resolve a legacy 'teal' to the reserved
// #2E8FA1. The slate fallback below covers any genuinely-corrupt
// row (shouldn't occur post-whitelist) so the UI doesn't crash.
//
// Optional `onClick` makes it a button (picker/apply),
// `onRemove` renders an ×, and `highlight` ({start,len}) tints the matched
// substring without altering the accessible name.

export const resolveTagHex = (c) => {
  if (typeof c === 'string' && /^#[0-9a-fA-F]{6}$/.test(c)) return c;
  return '#9197A8'; // slate — neutral fallback if DB ever yields a non-hex value
};

// "#RRGGBB" / "#RGB" -> "r, g, b" for rgba() tints. Neutral grey on bad input.
const rgbTriplet = (hex) => {
  let h = String(hex).replace('#', '');
  if (h.length === 3) h = h.split('').map(ch => ch + ch).join('');
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return '218, 220, 205';
  const n = parseInt(h, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
};

function renderName(name, highlight) {
  if (!highlight || highlight.len <= 0 || highlight.start < 0) return name;
  const { start, len } = highlight;
  return (
    <>
      {name.slice(0, start)}
      <span className="bg-[#92D400]/20 text-ink rounded-[2px] px-0.5">{name.slice(start, start + len)}</span>
      {name.slice(start + len)}
    </>
  );
}

// `variant` controls the visual treatment, NOT the stored colour:
//   'coloured' (default) — full hex-tinted bg + border + text. Used in
//                          pickers, list filters, and anywhere colour
//                          helps scanning across many records.
//   'muted'              — transparent bg, neutral grey text, subtle
//                          border, × hover-only. Used on the brief detail
//                          page where uniform calm beats rainbow.
//                          The tag's colour is still stored — only the
//                          render changes.
export default function TagChip({ tag, name, colour, onClick, onRemove, highlight, className = '', title, variant = 'coloured' }) {
  const label = tag?.name ?? name ?? '';
  const hex = resolveTagHex(tag?.colour ?? colour);
  const rgb = rgbTriplet(hex);
  const isMuted = variant === 'muted';
  const style = isMuted
    ? undefined
    : { color: hex, backgroundColor: `rgba(${rgb}, 0.10)`, borderColor: `rgba(${rgb}, 0.45)` };
  const mutedClass = isMuted
    ? 'bg-transparent border-line text-fusion-mid-grey hover:border-[#DADCCD] group/tag transition-colors'
    : '';
  // Softer register vs status pills: tinted fill + 45%-border + Title Case
  // (capitalize) — NOT uppercase. A small leading dot in the tag's own
  // colour makes the colour-coding obvious without screaming. Status pills
  // stay UPPERCASE + bolder weight so the two never read as the same class.
  const base = `inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-[0.02em] capitalize border font-['Metropolis'] ${mutedClass} ${className}`;

  // Leading colour dot — 6px filled circle in the tag's hex. Skipped on the
  // muted variant (brief-detail uniform-calm context) and on highlight rows
  // (the substring tint is the visual cue there).
  const dot = !isMuted ? (
    <span
      aria-hidden="true"
      className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
      style={{ backgroundColor: hex }}
    />
  ) : null;

  const inner = (
    <>
      {dot}
      <span className="truncate max-w-[200px]">{renderName(label, highlight)}</span>
      {onRemove && (
        <span
          role="button"
          tabIndex={0}
          aria-label={`Remove ${label}`}
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onRemove(); } }}
          className={`leading-none cursor-pointer -mr-0.5 ${isMuted ? 'text-[#51534A] hover:text-[#E0290A] opacity-0 group-hover/tag:opacity-100 transition-opacity font-bold' : 'text-[#51534A] hover:text-ink font-extrabold'}`}
        >
          ×
        </span>
      )}
    </>
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} title={title || label} style={style} className={`${base} ${isMuted ? '' : 'hover:brightness-125'} transition-[filter]`}>
        {inner}
      </button>
    );
  }
  return <span title={title || label} style={style} className={base}>{inner}</span>;
}
